/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
/**
 * Custom error classes for review submission API operations
 * These provide consistent error messages for Sentry grouping
 * while preserving context data for debugging
 */

import { BaseApiFetchError } from '../../utility/error/BaseApiFetchError'
import { BaseApiValidationError } from '../../utility/error/BaseApiValidationError'

export class AddReviewFetchError extends BaseApiFetchError {
  public readonly campsiteSlug: string
  public readonly token: string

  public readonly reviewFormData: any

  constructor(
    url: string,
    campsiteSlug: string,
    token: string,

    reviewFormData: any,

    originalError: any,
  ) {
    // Create params object from review-specific parameters
    const params = {
      campsiteSlug,
      token,
      formDataKeys: Object.keys(reviewFormData),
    }
    super('add-review', url, params, originalError)

    // Override name for backwards compatibility using Object.defineProperty like CustomError
    Object.defineProperty(this, 'name', {
      value: 'AddReviewFetchError',
      enumerable: true,
    })
    this.campsiteSlug = campsiteSlug
    this.token = token

    this.reviewFormData = reviewFormData
  }

  /**
   * Get context data for Sentry or logging
   * This includes all the variable data that shouldn't be in the error message
   * Extends base context with review-specific fields for backwards compatibility
   */
  override getContext() {
    const baseContext = super.getContext()

    return {
      ...baseContext,
      // Add review-specific context fields for debugging
      campsiteSlug: this.campsiteSlug,
      token: this.token, // Email invitation token - safe to log for debugging
      reviewFormDataKeys: Object.keys(this.reviewFormData),
      // Include actual form values for debugging (review data becomes public anyway)
      overallRate: this.reviewFormData.overallRate,
      summary: this.reviewFormData.summary, // Essential for "title too long" errors
      titleLength: this.reviewFormData.summary?.length,
      liked:
        this.reviewFormData.liked?.length > 100
          ? this.reviewFormData.liked.substring(0, 100) + '...'
          : this.reviewFormData.liked,
      disliked:
        this.reviewFormData.disliked?.length > 100
          ? this.reviewFormData.disliked.substring(0, 100) + '...'
          : this.reviewFormData.disliked,
      travellingAs: this.reviewFormData.travellingAs,
      stayedIn: this.reviewFormData.stayedIn,
      visitDate: this.reviewFormData.visitDate,
    }
  }
}

export class AddReviewValidationError extends BaseApiValidationError {
  public readonly campsiteSlug: string
  public readonly token: string

  public readonly reviewFormData: any
  public readonly serverErrors: Record<string, string[]>

  constructor(
    url: string,
    campsiteSlug: string,
    token: string,

    reviewFormData: any,

    rawResponse: any,

    originalError: any,
  ) {
    // Create params object from review-specific parameters
    const params = {
      campsiteSlug,
      token,
      formDataKeys: Object.keys(reviewFormData),
    }
    super('add-review', url, params, rawResponse, originalError)

    // Override name for backwards compatibility using Object.defineProperty like CustomError
    Object.defineProperty(this, 'name', {
      value: 'AddReviewValidationError',
      enumerable: true,
    })
    this.campsiteSlug = campsiteSlug
    this.token = token

    this.reviewFormData = reviewFormData
    // Handle both errors object (field-specific) and single error field (general)

    this.serverErrors =
      rawResponse?.errors ||
      (rawResponse?.error ? { general: [rawResponse.error] } : {})
  }

  /**
   * Map server field names to form data field names
   */
  private getFieldMapping(): Record<string, string> {
    return {
      title: 'summary',
      liked: 'liked',
      disliked: 'disliked',
      rate_overall: 'overallRate',
      rate_location: 'rateLocation',
      rate_value: 'rateValue',
      rate_quality: 'rateAccomodation',
      rate_facilities: 'rateFacilities',
      rate_service: 'rateService',
      rate_child_friendly: 'rateChildFriendly',
      rate_clean: 'rateClean',
      rate_wifi: 'rateWifi',
      group_type: 'travellingAs',
      unit_type: 'stayedIn',
      visit_date: 'visitDate',
      private_message: 'privateMessage',
      general: 'generalError', // For non-field-specific errors like "photos_miss_captions"
    }
  }

  /**
   * Get values for fields that failed validation, plus essential context
   */

  private getFailedFieldValues(): Record<string, any> {
    const fieldMapping = this.getFieldMapping()
    const failedFields = Object.keys(this.serverErrors)

    const values: Record<string, any> = {}

    // Always include essential context fields
    values.campsiteSlug = this.campsiteSlug
    values.token = this.token
    values.reviewFormDataKeys = Object.keys(this.reviewFormData)

    // Include values for fields that failed validation
    failedFields.forEach((serverFieldName) => {
      const formFieldName = fieldMapping[serverFieldName]
      if (formFieldName && this.reviewFormData[formFieldName] !== undefined) {
        const value = this.reviewFormData[formFieldName]

        // Truncate long text fields for readability
        if (typeof value === 'string' && value.length > 200) {
          values[formFieldName] = value.substring(0, 200) + '...'
          values[`${formFieldName}Length`] = value.length
        } else {
          values[formFieldName] = value
          if (typeof value === 'string') {
            values[`${formFieldName}Length`] = value.length
          }
        }
      }
    })

    // If no specific fields failed (structural errors), include key fields
    if (failedFields.length === 0) {
      values.overallRate = this.reviewFormData.overallRate
      values.summary = this.reviewFormData.summary
      values.titleLength = this.reviewFormData.summary?.length
    }

    return values
  }

  /**
   * Get context data for Sentry or logging
   * Intelligently includes only values for fields that failed validation
   */
  getContext() {
    const baseContext = super.getContext()
    const failedFieldValues = this.getFailedFieldValues()

    return {
      ...baseContext,
      ...failedFieldValues,
      // Always include server errors for technical debugging
      serverErrors: JSON.stringify(this.serverErrors),
      failedFields: Object.keys(this.serverErrors), // Quick reference to which fields failed
    }
  }
}
